'use client'

import { Button, Checkbox, Input, LuneTheme, useDevice } from '@lune-fe/lune-ui-lib'
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight'
import Box from '@mui/material/Box'
import Text from '@mui/material/Typography'
import { FC, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

import TwoColumnsWithBackgroundImage from '@/components/TwoColumnsWithBackgroundImage'
import { useNewsletter } from '@/hooks/useNewsLetter'

// eslint-disable-next-line complexity
const RequestDemoForm: FC<{ hubspotFormId: string }> = ({ hubspotFormId }) => {
    const { palette } = LuneTheme
    const { smScreen, xsScreen, mdScreen } = useDevice()
    const { isValidEmail, toHubspot } = useNewsletter(
        hubspotFormId,
        [
            'Request a demo',
            'Accelerate revenue by meeting your sustainability goals. Let’s connect to explore your carbon credit and emission calculation needs.',
            'I am happy to receive marketing communications from Lune',
        ],
        { gtagEvent: 'form_submission' },
    )

    const [name, setName] = useState<string>('')
    const [lastName, setLastName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [company, setCompany] = useState<string>('')
    const [nameError, setNameError] = useState<boolean>(false)
    const [lastNameError, setLastNameError] = useState<boolean>(false)
    const [emailError, setEmailError] = useState<boolean>(false)
    const [companyError, setCompanyError] = useState<boolean>(false)
    const [marketingConsent, setMarketingConsent] = useState<boolean>(false)
    const [success, setSuccess] = useState<boolean>()
    const [debouncedEmail] = useDebounce(email, 1000)

    useEffect(() => {
        if (isValidEmail(email) || email === '') {
            setEmailError(false)
        } else {
            setEmailError(true)
        }
    }, [debouncedEmail, email, isValidEmail])

    useEffect(() => {
        setNameError(false)
    }, [name])

    useEffect(() => {
        setLastNameError(false)
    }, [lastName])

    const onSubmit = async () => {
        const validName = name.length
        const validLastName = lastName.length
        const validEmail = isValidEmail(email)
        const validCompany = company.length

        if (!validName || !validEmail || !validCompany) {
            !validName && setNameError(true)
            !validLastName && setLastNameError(true)
            !validEmail && setEmailError(true)
            !validCompany && setCompanyError(true)

            return
        }

        const data = await toHubspot({
            firstname: name,
            lastname: lastName,
            email,
            company,
            legalConsent: 'gatedContent',
        })
        if (data.status === 'error') {
            setSuccess(false)
        } else {
            setSuccess(true)
            setName('')
            setLastName('')
            setEmail('')
            setCompany('')
            setMarketingConsent(false)

            gtag('event', 'request_demo', {})
        }
    }

    return (
        <TwoColumnsWithBackgroundImage
            backgroundImage={'/images/landing-request-demo-desktop.png'}
            mobileBackgroundImage={'/images/landing-request-demo-mobile.png'}
            rightSide={<Box sx={{ height: smScreen ? '268px' : xsScreen ? '268px' : 'auto' }} />}
            leftSide={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                        gap: mdScreen ? 3 : 4,
                    }}
                >
                    {success ? (
                        <Box
                            sx={{
                                height: mdScreen ? '508px' : '572px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: mdScreen ? 4 : 9,
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}
                        >
                            <Text variant={mdScreen ? 'h5' : 'h4'}>
                                Thank you, someone from the team will get in touch!
                            </Text>
                        </Box>
                    ) : (
                        <>
                            <Text variant={mdScreen ? 'h5' : 'h4'}>Request a demo</Text>
                            <Text variant={mdScreen ? 'body2' : 'body1'}>
                                Accelerate revenue by meeting your sustainability goals. Let’s
                                connect to explore your carbon credit and emission calculation
                                needs.
                            </Text>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    flexDirection: 'column',
                                    gap: 1,
                                    width: '100%',
                                    '.MuiInputBase-root ': {
                                        width: '100%',
                                    },
                                }}
                            >
                                <Input
                                    placeholder={'Name'}
                                    error={nameError}
                                    value={name}
                                    sx={{ backgroundColor: palette.White }}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <Input
                                    placeholder={'Last name'}
                                    error={lastNameError}
                                    value={lastName}
                                    sx={{ backgroundColor: palette.White }}
                                    onChange={(e) => setLastName(e.target.value)}
                                />
                                <Input
                                    placeholder={'Company name'}
                                    error={companyError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={company}
                                    onChange={(e) => setCompany(e.target.value)}
                                />
                                <Input
                                    placeholder={'Email'}
                                    error={emailError}
                                    sx={{ backgroundColor: palette.White }}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                                <Box pt={4}>
                                    <Checkbox
                                        sx={{ marginRight: '12px' }}
                                        checked={marketingConsent}
                                        onChange={(e) => setMarketingConsent(e.target.checked)}
                                    />

                                    <Text
                                        variant="body3"
                                        onClick={() => setMarketingConsent(!marketingConsent)}
                                        sx={{ '&:hover': { cursor: 'pointer' } }}
                                    >
                                        I am happy to receive marketing communications from Lune
                                    </Text>
                                </Box>
                            </Box>
                            <Button
                                onClick={onSubmit}
                                leftIcon={<ArrowCircleRightIcon />}
                                sx={{ width: { xs: '100%', sm: 'auto' } }}
                            >
                                Request a demo
                            </Button>
                        </>
                    )}
                </Box>
            }
        />
    )
}

export default RequestDemoForm
